import Button from '@/component/Button'
import Form, { Field } from '@/component/FieldForm'
import Toast from '@/component/Toast'
import TGASDK from '@/config/TGASDK'
import { PopUpStore } from '@/store/PopupStore'
import { UC } from '@kulee/tga-sdk'
import { useMutation } from '@tanstack/react-query'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import EmailInput from '../../component/EmailInput'
import PasswordInput from '../../component/PasswordInput'
import VerificationCodeInput from '../../component/VerificationCodeInput'

const ResetPasswordMain: React.FC<PropType> = () => {
  const { t } = useTranslation('uc')

  const mutation = useMutation({
    mutationFn: (values: FormValuesType) => {
      const { email, password, code } = values

      return TGASDK.module.UC.service.resetUserPassword({
        email,
        newPassword: password,
        code: code
      })
    }
  })

  const handleResetPassword = async (values: FormValuesType) => {
    mutation.mutate(values, {
      onSuccess: () => {
        PopUpStore.setResetPasswordPopupVisible(false)
        Toast.show({
          content: t('message.resetPasswordSuccess'),
          icon: 'success'
        })
      },
      onError: (error: any) => {
        Toast.show({
          content: t('message.resetPasswordFail'),
          icon: 'error'
        })
      }
    })
  }

  useEffect(() => {
    TGASDK.addModule(UC)
  }, [])

  return (
    <div className="flex flex-col gap-md">
      <div className="text-lg font-bold text-center">{t('tip.resetPasswordWelcome')}</div>
      <div>
        <Form onFinish={handleResetPassword}>
          {(values, form) => {
            const emailError = form.getFieldError('email')
            const codeError = form.getFieldError('code')
            const passwordError = form.getFieldError('password')
            const passwordComfirmError = form.getFieldError('passwordComfirm')

            return (
              <div className="flex flex-col gap-sm">
                <div className="flex flex-col gap-[2px]">
                  <Field<FormValuesType>
                    name="email"
                    validateDebounce={500}
                    validateFirst={true}
                    rules={[
                      {
                        required: true,
                        pattern: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
                        message: t('message.emailFormatError')
                      },
                      {
                        validator: async (_: any, value: string) => {
                          const { data } = await TGASDK.module.UC.service.checkAccountExist(value)
                          if (!data) {
                            return Promise.reject(t('message.accountNotExistsError'))
                          }
                          return Promise.resolve()
                        }
                      }
                    ]}
                  >
                    <EmailInput placeholder={t('placeholder.email')} />
                  </Field>
                  <p className="h-4 overflow-hidden text-semantic-error px-sm">{emailError[0]}</p>
                </div>
                <div className="flex flex-col gap-[2px]">
                  <Field<FormValuesType>
                    name="code"
                    rules={[
                      {
                        required: true,
                        message: t('message.codeFormatError')
                      }
                    ]}
                  >
                    <VerificationCodeInput
                      placeholder={t('placeholder.code')}
                      email={emailError.length === 0 && values.email ? values.email : undefined}
                      grantType="CHANGE_PASSWORD"
                    />
                  </Field>
                  <p className="h-4 overflow-hidden text-semantic-error px-sm">{codeError[0]}</p>
                </div>
                <div className="flex flex-col gap-[2px]">
                  <Field<FormValuesType>
                    name="password"
                    rules={[
                      {
                        required: true,
                        pattern: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*?&]{6,}$/,
                        message: t('message.passwordFormatError')
                      }
                    ]}
                  >
                    <PasswordInput />
                  </Field>
                  <p className="h-4 overflow-hidden text-semantic-error px-sm">{passwordError[0]}</p>
                </div>
                <div className="flex flex-col gap-[2px]">
                  <Field<FormValuesType>
                    name="passwordComfirm"
                    rules={[
                      context => ({
                        validator(rule, value) {
                          const { password } = context.getFieldsValue(true)
                          return password === value
                            ? Promise.resolve()
                            : Promise.reject(t('message.passwordConfirmError'))
                        }
                      })
                    ]}
                  >
                    <PasswordInput placeholder={t('placeholder.passwordConfirm')} />
                  </Field>
                  <p className="h-4 overflow-hidden text-semantic-error px-sm">{passwordComfirmError[0]}</p>
                </div>
                <div className="mt-md">
                  <Button htmlType="submit" type="primary" className="w-full" loading={mutation.isPending}>
                    {t('btn.resetPassword')}
                  </Button>
                </div>
              </div>
            )
          }}
        </Form>
      </div>
    </div>
  )
}

interface PropType {}

interface FormValuesType {
  email: string
  code: string
  password: string
  passwordComfirm: string
}

export default ResetPasswordMain
